import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Typography,
  Box,
  Button,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ButtonBase,
} from '@mui/material';
import {
  VpnKey,
  Search,
  Error,
  Refresh,
  InfoOutlined,
  LocationOn,
  Lock,
  Google,
} from '@mui/icons-material';
import { WarehouseAPI } from '../services/api';
import { AccessControlUser, LoginEvent, UserLoginHistory } from '../types';
import { formatDate } from '../utils/formatDate';
import { roleColor, roleLabel } from '../components/actionables/roles';
import { display, eyebrow } from '../components/actionables/brand';
import PageContainer from '../components/common/PageContainer';
import PageHeader from '../components/common/PageHeader';
import StatPill from '../components/common/StatPill';
import ListRowCard from '../components/common/ListRowCard';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../components/common/TablePaginationBar';
import { useUrlParam, useSetUrlParams } from '../components/common/useUrlParam';

// Render an approximate, IP-derived location honestly. Any/all parts may be null (private
// IP, lookup failed, slow resolve). Falls back to the IP, then to a dash when nothing is
// known. Used by the list rows (no `resolved` outcome available on the snapshot).
const formatLocation = (
  location: AccessControlUser['lastLoginLocation'],
  ip: string | null,
  hasLogin: boolean,
): string => {
  if (!hasLogin) return '—';
  const parts = location
    ? [location.city, location.region, location.country].filter(Boolean)
    : [];
  if (parts.length > 0) return parts.join(', ');
  // No location parts resolved but we have a sign-in: private/unknown network.
  return ip ? 'Local/Unknown' : '—';
};

// Render a LoginEvent's location using the `resolved` outcome so the timeline distinguishes
// "Local/Unknown" from "not yet resolved" from "lookup failed".
const formatEventLocation = (location: LoginEvent['location']): string => {
  if (!location) return 'Location unavailable';
  const parts = [location.city, location.region, location.country].filter(Boolean);
  if (parts.length > 0) return parts.join(', ');
  switch (location.resolved) {
    case 'local':
      return 'Local/Unknown';
    case 'pending':
      return 'Resolving…';
    case 'failed':
    case 'resolved':
    default:
      return 'Location unavailable';
  }
};

// Numeric IST (UTC+5:30) day index: the count of whole IST-days since the Unix epoch. Comparing
// two indices tells us same-day / adjacent-day without leaning on the host timezone. Shift the
// instant by the IST offset, then floor to the day.
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const DAY_MS = 86_400_000;
function istDayIndex(ms: number): number {
  return Math.floor((ms + IST_OFFSET_MS) / DAY_MS);
}

// True when `iso` falls on the same IST calendar day as now.
const isActiveTodayIST = (iso: string | null): boolean =>
  iso
    ? Number.isNaN(Date.parse(iso))
      ? false
      : istDayIndex(Date.parse(iso)) === istDayIndex(Date.now())
    : false;

// Human, compact relative time. '—' for null/unparseable; 'just now' within a minute (negative
// deltas — a future timestamp from minor clock skew — clamp to 0 so they read 'just now', never
// 'in 3m'); minutes/hours; 'Yesterday' when the IST day index is exactly one behind now; else the
// full formatted date.
export function timeAgo(iso: string | null, now = Date.now()): string {
  if (!iso) return '—';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';
  const delta = Math.max(0, now - then);
  if (delta < 60_000) return 'just now';
  const mins = Math.floor(delta / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(delta / 3_600_000);
  if (hours < 24) return `${hours}h ago`;
  if (istDayIndex(then) === istDayIndex(now) - 1) return 'Yesterday';
  return formatDate(iso);
}

// A user counts as "online" when their last presence ping landed within the window. A negative
// delta (future timestamp from clock skew) is treated as online.
const ONLINE_WINDOW_MS = 5 * 60 * 1000;
function isOnline(lastSeenAt: string | null, now = Date.now()): boolean {
  return lastSeenAt != null && now - new Date(lastSeenAt).getTime() < ONLINE_WINDOW_MS;
}

// One shared column template so the md+ header row and every data row line up exactly.
// Columns: User | Last sign-in | Last seen | IP address | Location. (No Device column — the
// GET /users snapshot carries no per-user device; device is shown per-event in the drilldown.)
// The two time columns are sized to fit the longest realistic formatDate output
// ('26 Oct 2026, 07:54 am', ~21 chars) on ONE line at the body font size, so the older-entry
// absolute-date fallback no longer clips. User/Location fr units are trimmed slightly so the
// extra width doesn't overflow a standard ~1280-1440px viewport.
const ROW_GRID = {
  xs: '1fr',
  md: 'minmax(200px,2.1fr) 200px 200px 160px minmax(180px,1.5fr)',
} as const;

const columnLabelSx = { ...eyebrow, color: '#8A8078', fontSize: '0.58rem' } as const;

const AccessControl: React.FC = () => {
  const reduceMotion = useReducedMotion();

  const [users, setUsers] = useState<AccessControlUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Search + page live in the URL (prefix "ac") so a refresh restores where the user was. Defaults
  // stay OUT of the URL; changing the search resets the page in the SAME update.
  const setUrlParams = useSetUrlParams();
  const [searchTerm] = useUrlParam('acq', '');
  const setSearchTerm = (v: string) => setUrlParams({ acq: v === '' ? null : v, acpage: null });
  const [pageParam, setPageParam] = useUrlParam('acpage', '0');
  const page = parseInt(pageParam, 10) || 0;
  const setPage = (v: number) => setPageParam(v === 0 ? '0' : String(v));

  // Drill-down dialog state.
  const [selectedUser, setSelectedUser] = useState<AccessControlUser | null>(null);
  const [history, setHistory] = useState<UserLoginHistory | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await WarehouseAPI.getAccessControlUsers();
      setUsers(data);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Client-side filter by name OR email.
  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q),
    );
  }, [users, searchTerm]);

  const pagedUsers = useMemo(
    () => filteredUsers.slice(page * DEFAULT_PAGE_SIZE, page * DEFAULT_PAGE_SIZE + DEFAULT_PAGE_SIZE),
    [filteredUsers, page],
  );

  // Summary stats + the online subset are computed over the FULL users set, never the paged
  // slice, so the numbers reflect everyone regardless of search/pagination.
  const onlineUsers = useMemo(
    () => users.filter((u) => isOnline(u.lastSeenAt)),
    [users],
  );
  const activeTodayCount = useMemo(
    () => users.filter((u) => isActiveTodayIST(u.lastLoginAt)).length,
    [users],
  );
  const neverSignedInCount = useMemo(
    () => users.filter((u) => u.lastLoginAt == null).length,
    [users],
  );

  // Lazily fetch the selected user's login history, keyed by user id. Re-invokable by Retry.
  const loadHistory = useCallback(async (userId: string) => {
    setHistoryLoading(true);
    setHistoryError(false);
    try {
      const data = await WarehouseAPI.getUserLoginHistory(userId);
      setHistory(data);
    } catch (err) {
      setHistoryError(true);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleOpenUser = (user: AccessControlUser) => {
    setSelectedUser(user);
    setHistory(null);
    loadHistory(user._id);
  };

  const handleCloseDialog = () => {
    setSelectedUser(null);
    setHistory(null);
    setHistoryError(false);
    setHistoryLoading(false);
  };

  // Only paint the stat row + online section once the first load has resolved — avoids a flash
  // of all-zero stats and an empty online section while loading.
  const dataReady = !loading && !error;

  return (
    <PageContainer>
      <PageHeader
        icon={<VpnKey />}
        eyebrow="ADMIN"
        title="Access Control"
        subtitle="Who's online now, each user's last sign-in and last-seen time, the sign-in IP, and approximate IP-based location. Admins only."
      />

      {/* Toolbar: search + approximate-location note */}
      <Box
        component={motion.div}
        initial={reduceMotion ? false : { opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}
      >
        <TextField
          size="small"
          placeholder="Search by name or email…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 260, flex: '1 1 260px', '& .MuiOutlinedInput-root': { borderRadius: '999px', bgcolor: '#fff' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: '#8A8078' }} />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip title="Locations are approximate and derived from the sign-in IP address. They may be inaccurate or unavailable.">
          <InfoOutlined sx={{ fontSize: 18, color: '#8A8078' }} />
        </Tooltip>
      </Box>

      {/* Summary stat row — computed over the full users set, painted only once loaded. */}
      {dataReady && (
        <Box
          component={motion.div}
          initial={reduceMotion ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}
        >
          <StatPill variant="summary" label="Total users" value={users.length} accent="#1A1410" />
          <StatPill variant="summary" label="Online now" value={onlineUsers.length} accent="#16A34A" />
          <StatPill variant="summary" label="Active today" value={activeTodayCount} accent="#DC5829" />
          <StatPill variant="summary" label="Never signed in" value={neverSignedInCount} accent="#8A8078" />
        </Box>
      )}

      {/* Online-now section — rendered only when at least one user is online. */}
      {dataReady && onlineUsers.length > 0 && (
        <Box
          component={motion.div}
          initial={reduceMotion ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          sx={{ mb: 2.5 }}
        >
          <Typography sx={{ ...eyebrow, color: '#16A34A', mb: 1 }}>
            Online now · active in the last 5 minutes
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {onlineUsers.map((u) => {
              const accent = roleColor(u.role);
              return (
                <Box
                  key={u._id}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.75,
                    pl: 1.25, pr: 1.5, py: 0.75, borderRadius: '999px',
                    bgcolor: '#fff', border: '1px solid #ECECE4',
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#16A34A', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
                    {u.name}
                  </Typography>
                  <Chip
                    label={roleLabel(u.role)}
                    size="small"
                    sx={{ fontWeight: 700, borderRadius: '999px', bgcolor: accent.bg, color: accent.text, height: 20 }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                    {timeAgo(u.lastSeenAt)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* User list */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={{ ...display('1.1rem'), color: 'text.primary' }}>
          Users ({filteredUsers.length})
        </Typography>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', py: 8, px: 3, borderRadius: '14px', bgcolor: 'background.paper',
            border: '1px dashed #ECECE4',
          }}
        >
          <CircularProgress sx={{ color: '#DC5829' }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading users…
          </Typography>
        </Box>
      ) : error ? (
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', py: 8, px: 3, borderRadius: '14px', bgcolor: 'background.paper',
            border: '1px dashed #ECECE4',
          }}
        >
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(220,38,38,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
            <Error sx={{ color: '#DC2626' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Couldn't load users
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            The access control service may be unavailable. Please try again.
          </Typography>
          <Button
            variant="contained"
            disableElevation
            startIcon={<Refresh />}
            onClick={() => loadUsers()}
            sx={{ py: 1, px: 2.5, fontWeight: 700, textTransform: 'none', borderRadius: '999px' }}
          >
            Retry
          </Button>
        </Box>
      ) : filteredUsers.length === 0 ? (
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', py: 8, px: 3, borderRadius: '14px', bgcolor: 'background.paper',
            border: '1px dashed #ECECE4',
          }}
        >
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: searchTerm ? '#f4f1ea' : 'rgba(220,88,41,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
            {searchTerm ? <Search sx={{ color: '#8A8078' }} /> : <VpnKey sx={{ color: '#DC5829' }} />}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {searchTerm ? 'No users found' : 'No users to display'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {searchTerm ? 'Try a different name or email.' : 'There are no users to show right now.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Column header row — md+ only; shares ROW_GRID with the data rows so columns align. */}
          <Box
            sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: ROW_GRID,
              alignItems: 'center', columnGap: 2,
              pl: 3, pr: 2, py: 0.5,
            }}
          >
            <Typography sx={columnLabelSx}>User</Typography>
            <Typography sx={columnLabelSx}>Last sign-in</Typography>
            <Typography sx={columnLabelSx}>Last seen</Typography>
            <Typography sx={columnLabelSx}>IP address</Typography>
            <Typography sx={columnLabelSx}>Location</Typography>
          </Box>

          {pagedUsers.map((user, index) => {
            const accent = roleColor(user.role);
            const hasLogin = Boolean(user.lastLoginAt);
            const locationLabel = formatLocation(user.lastLoginLocation, user.lastLoginIp, hasLogin);
            const online = isOnline(user.lastSeenAt);
            return (
              <ListRowCard key={user._id} accent={accent.main} index={index}>
                <ButtonBase
                  onClick={() => handleOpenUser(user)}
                  aria-label={`View login history for ${user.name}`}
                  sx={{
                    width: '100%', textAlign: 'left', display: 'block', borderRadius: '14px',
                    '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: -2 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: ROW_GRID,
                      alignItems: 'center', columnGap: 2, rowGap: 0.5,
                      pl: 3, pr: 2, py: 1.25,
                    }}
                  >
                    {/* 1) User = name + email + role badge + online/offline status dot */}
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Tooltip title={online ? 'Online — active in the last 5 min' : 'Offline'}>
                          <Box
                            component="span"
                            sx={{
                              width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                              bgcolor: online ? '#16A34A' : 'transparent',
                              border: online ? 'none' : '1.5px solid #C9C2B8',
                            }}
                          />
                        </Tooltip>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }} noWrap>
                          {user.name}
                        </Typography>
                        <Chip
                          label={roleLabel(user.role)}
                          size="small"
                          sx={{ fontWeight: 700, borderRadius: '999px', bgcolor: accent.bg, color: accent.text, height: 20 }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                        {user.email}
                      </Typography>
                    </Box>

                    {/* 2) Last sign-in = relative, exact timestamp on hover */}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ ...columnLabelSx, display: { md: 'none' } }}>Last sign-in</Typography>
                      <Tooltip title={formatDate(user.lastLoginAt ?? undefined)}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.primary', fontWeight: 600,
                            whiteSpace: 'normal', overflow: 'hidden',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                            lineHeight: 1.25,
                          }}
                        >
                          {timeAgo(user.lastLoginAt)}
                        </Typography>
                      </Tooltip>
                    </Box>

                    {/* 3) Last seen = relative presence, exact on hover */}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ ...columnLabelSx, display: { md: 'none' } }}>Last seen</Typography>
                      <Tooltip title={user.lastSeenAt ? formatDate(user.lastSeenAt) : 'Never seen'}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary', fontWeight: 600,
                            whiteSpace: 'normal', overflow: 'hidden',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                            lineHeight: 1.25,
                          }}
                        >
                          {timeAgo(user.lastSeenAt)}
                        </Typography>
                      </Tooltip>
                    </Box>

                    {/* 4) IP ADDRESS = always-rendered, first-class monospace column */}
                    <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography component="span" sx={{ ...columnLabelSx, display: { md: 'none' } }}>IP:</Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.primary', fontFamily: 'monospace', fontWeight: 600 }}
                        noWrap
                      >
                        {hasLogin && user.lastLoginIp ? user.lastLoginIp : '—'}
                      </Typography>
                    </Box>

                    {/* 5) Location = approximate, IP-derived */}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ ...columnLabelSx, display: { md: 'none' } }}>Location</Typography>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', maxWidth: '100%' }}>
                        <LocationOn sx={{ fontSize: 14, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {locationLabel}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </ButtonBase>
              </ListRowCard>
            );
          })}
          <TablePaginationBar
            page={page}
            total={filteredUsers.length}
            rowsPerPage={DEFAULT_PAGE_SIZE}
            onPageChange={setPage}
          />
        </Box>
      )}

      {/* Login-history drill-down dialog */}
      <Dialog
        open={Boolean(selectedUser)}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedUser ? `Sign-in history — ${selectedUser.name}` : 'Sign-in history'}
          {selectedUser && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
              <Box
                sx={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  bgcolor: isOnline(selectedUser.lastSeenAt) ? '#16A34A' : 'transparent',
                  border: isOnline(selectedUser.lastSeenAt) ? 'none' : '1.5px solid #C9C2B8',
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {isOnline(selectedUser.lastSeenAt)
                  ? 'Online now'
                  : `Offline · last seen ${timeAgo(selectedUser.lastSeenAt)}`}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 500 }}>
            Locations are approximate and derived from the sign-in IP.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Box
              sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                textAlign: 'center', py: 6, px: 3, borderRadius: '14px', bgcolor: 'background.paper',
                border: '1px dashed #ECECE4',
              }}
            >
              <CircularProgress sx={{ color: '#DC5829' }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading login history…
              </Typography>
            </Box>
          ) : historyError ? (
            <Box
              sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                textAlign: 'center', py: 6, px: 3, borderRadius: '14px', bgcolor: 'background.paper',
                border: '1px dashed #ECECE4',
              }}
            >
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(220,38,38,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                <Error sx={{ color: '#DC2626' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Couldn't load login history
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                Please try again.
              </Typography>
              <Button
                variant="contained"
                disableElevation
                startIcon={<Refresh />}
                onClick={() => selectedUser && loadHistory(selectedUser._id)}
                sx={{ py: 1, px: 2.5, fontWeight: 700, textTransform: 'none', borderRadius: '999px' }}
              >
                Retry
              </Button>
            </Box>
          ) : !history || history.logins.length === 0 ? (
            <Box
              sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                textAlign: 'center', py: 6, px: 3, borderRadius: '14px', bgcolor: 'background.paper',
                border: '1px dashed #ECECE4',
              }}
            >
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(220,88,41,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                <VpnKey sx={{ color: '#DC5829' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                No sign-ins recorded yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                This user has not signed in since login tracking began.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, py: 1 }}>
              {history.logins.map((event) => (
                <Box
                  key={event._id}
                  sx={{
                    position: 'relative', borderRadius: '12px', border: '1px solid #ECECE4',
                    bgcolor: '#fff', px: 2, py: 1.25,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {formatDate(event.at)}
                    </Typography>
                    <Chip
                      icon={event.method === 'google' ? <Google sx={{ fontSize: 14 }} /> : <Lock sx={{ fontSize: 14 }} />}
                      label={event.method === 'google' ? 'Google' : 'Password'}
                      size="small"
                      sx={{ fontWeight: 700, borderRadius: '999px', height: 22 }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                    {event.device || 'Unknown device'}
                  </Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', mt: 0.25 }}>
                    <LocationOn sx={{ fontSize: 14, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {formatEventLocation(event.location)}
                      {event.ip ? ` · ${event.ip}` : ''}
                    </Typography>
                  </Box>
                </Box>
              ))}
              <Typography variant="caption" sx={{ color: '#8A8078', mt: 0.5 }}>
                Locations are approximate and derived from the sign-in IP address.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default AccessControl;
